/**
 * Curve editor actions shared by the commands, the header buttons and the canvas double-clicks (W2-04b
 * curve-editor; DESIGN.md §4.7, §5.6.1-5.6.3).
 *
 * Every document change runs ONE `DocStore.transact` with a core command body; failures and the notes a body
 * attaches (approximate splits, skipped segments) are shown as toasts. The expanded column lives in the editor
 * store (`timeline.expandedColumn`, W2-04a's id format); opening it also focuses that column.
 */

import { DIALOG } from '../../app/dialogs/types';
import type { AppServices } from '../../app/registry/types';
import { Core } from '../../core/commands/index';
import { Ease } from '../../core/ease/index';
import type { BezierPoints, Ease as EaseValue } from '../../core/ease/types';
import { Defaults } from '../../core/model/defaults';
import type { KeyId, LineProp } from '../../core/model/types';
import type { TxBuilder } from '../../core/ops/types';
import { Beats } from '../../core/time/beat';
import type { Beat } from '../../core/time/types';
import type { MessageRef } from '../../core/util/types';
import type { ColumnRef } from '../../state/types';
import { niceStep } from '../../render/timeline/motion/curveEditor';
import { asTimelineHostController } from '../../render/timeline/TimelineHost';
import { m } from './curveEditor.messages';
import {
  CURVE_DEF_ID,
  MAX_UI_LAYERS,
  bezierAllowed,
  columnOfKey,
  easeUseFor,
  fitBezier,
  isLayered,
  isNumericProp,
  layerCount,
  motionColumnId,
  parseMotionColumnId,
  propLabel,
  trackKeys,
  unitContextOf,
} from './curveModel';

type Services = Pick<AppServices, 'store' | 'editor' | 'engine' | 'ui' | 'i18n' | 'timeline' | 'registry' | 'settings'>;

// ---------------------------------------------------------------------------------------------------------------
// Expanded column
// ---------------------------------------------------------------------------------------------------------------

/** The expanded column when it can be shown (project open, line exists, numeric property), else null. */
export function expandedTarget(s: Pick<Services, 'store' | 'editor'>): ColumnRef | null {
  const ref = parseMotionColumnId(s.editor.getState().timeline.expandedColumn);
  const store = s.store;
  if (ref === null || store === null) return null;
  if (!store.doc.lines.has(ref.line) || !isNumericProp(ref.prop)) return null;
  return ref;
}

/** Why a column cannot be opened in the curve editor, or null. */
export function cannotExpand(s: Pick<Services, 'store' | 'i18n'>, ref: ColumnRef | null): MessageRef | null {
  const store = s.store;
  if (store === null) return { key: m.noProject };
  if (ref === null) return { key: m.noFocusedColumn };
  if (!store.doc.lines.has(ref.line)) return { key: m.lineMissing };
  if (!isNumericProp(ref.prop)) {
    const prop = propLabel(ref.prop, unitContextOf(store.doc, store.idx, ref.line), s.i18n);
    return { key: m.notNumeric, params: { prop } };
  }
  return null;
}

function relayout(s: Pick<Services, 'timeline'>): void {
  s.timeline?.relayout();
}

/** Opens the curve editor on a column (and focuses that column). */
export function expandColumn(s: Services, ref: ColumnRef): boolean {
  const why = cannotExpand(s, ref);
  if (why !== null) {
    s.ui.toast(why, { kind: 'warning' });
    return false;
  }
  const st = s.editor.getState();
  st.setFocusedColumn(ref);
  if (st.activeLine === ref.line && isLayered(ref.prop)) st.setActiveLayer(ref.layer);
  st.setTimeline({ expandedColumn: motionColumnId(ref) });
  relayout(s);
  revealEditor(s);
  return true;
}

/** Scrolls the columns area so the expanded editor is in view (it may start right of the visible columns). */
function revealEditor(s: Pick<Services, 'timeline'>): void {
  const reveal = (): void => {
    const host = s.timeline === null ? null : asTimelineHostController(s.timeline);
    if (host === null) return;
    try {
      const col = host.getLayout().find((l) => l.defId === CURVE_DEF_ID);
      if (col !== undefined) host.revealColumn(col.id);
    } catch {
      // not laid out yet
    }
  };
  reveal();
  // The host lays the new column out on its next frame: reveal again once it has a place.
  if (typeof requestAnimationFrame === 'function') requestAnimationFrame(reveal);
}

export function collapseEditor(s: Pick<Services, 'editor' | 'timeline'>): void {
  if (s.editor.getState().timeline.expandedColumn === null) return;
  s.editor.getState().setTimeline({ expandedColumn: null });
  relayout(s);
}

/** Shows another layer of the expanded property. */
export function switchLayer(s: Services, layer: number): void {
  const cur = expandedTarget(s);
  if (cur === null || !isLayered(cur.prop) || !Number.isSafeInteger(layer) || layer < 0) return;
  expandColumn(s, { ...cur, layer });
}

// ---------------------------------------------------------------------------------------------------------------
// Transactions
// ---------------------------------------------------------------------------------------------------------------

/** Runs one transaction; toasts the failure reason or the first note. Returns the body's value, or null. */
export function runTx<R>(
  s: Pick<Services, 'store' | 'ui'>,
  label: MessageRef,
  build: (tx: TxBuilder) => R,
  command?: string,
): R | null {
  const store = s.store;
  if (store === null) {
    s.ui.toast({ key: m.noProject }, { kind: 'warning' });
    return null;
  }
  const out = store.transact({ label, ...(command === undefined ? {} : { command }) }, build);
  if (!out.ok) {
    s.ui.toast(out.reason, { kind: 'error' });
    return null;
  }
  const note = out.notes[0];
  if (note !== undefined) s.ui.toast(note, { kind: 'info', undo: true });
  return out.value;
}

/** True when the line is locked in the editor (preview filter: blocks editing only). */
export function lineLocked(s: Pick<Services, 'editor'>, ref: ColumnRef): boolean {
  return s.editor.getState().filters.lockedLines.has(ref.line);
}

/** Value of a new track's first key that keeps the motion as it is (DESIGN.md §6.6). */
function neutralValue(s: Pick<Services, 'store'>, ref: ColumnRef): number {
  const store = s.store;
  if (store === null || ref.layer > 0) return 0;
  const ctx = unitContextOf(store.doc, store.idx, ref.line);
  const v = Defaults.leadInValue(ref.prop, 0, ctx.kind, ctx.uiMover);
  if (v !== null) return v;
  return ref.prop === 'scaleX' || ref.prop === 'scaleY' ? 1 : 0;
}

/**
 * Adds a key at `t` on a column without changing the motion (shape-preserving split; a missing track is created
 * with the value that looks like "no track") and selects it.
 */
export function addKeyAt(s: Services, ref: ColumnRef, t: Beat): KeyId | null {
  if (lineLocked(s, ref)) {
    s.ui.toast({ key: m.lineLocked }, { kind: 'warning' });
    return null;
  }
  const store = s.store;
  if (store === null) return null;
  const track = store.idx.track(ref.line, ref.prop, ref.layer);
  const key = runTx(s, { key: m.txAddKey }, (tx) => {
    if (track !== undefined) return Core.keys.insertKey(tx, { track, t }).key;
    const created = Core.keys.ensureTrack(tx, {
      owner: { kind: 'line', line: ref.line, prop: ref.prop, layer: ref.layer },
      value: neutralValue(s, ref),
      at: t,
    });
    for (const id of tx.idx.keysByTrack(created)) {
      const k = tx.doc.keys.get(id);
      if (k !== undefined && Beats.eq(k.t, t)) return k.id;
    }
    return null;
  });
  if (key !== null) {
    const st = s.editor.getState();
    st.select({ keys: new Set([key]), primary: { kind: 'key', id: key } }, 'replace');
    st.setFocusedColumn(ref);
  }
  return key;
}

/** Selected segment start keys that still have a next key (in selection order). */
export function selectedSegments(s: Pick<Services, 'store' | 'editor'>): KeyId[] {
  const store = s.store;
  if (store === null) return [];
  const out: KeyId[] = [];
  for (const id of s.editor.getState().selection.segments) {
    if (store.doc.keys.has(id) && store.idx.nextKey(id) !== undefined) out.push(id);
  }
  return out;
}

export function selectedKeys(s: Pick<Services, 'store' | 'editor'>): KeyId[] {
  const store = s.store;
  if (store === null) return [];
  return [...s.editor.getState().selection.keys].filter((id) => store.doc.keys.has(id));
}

/** True when one of the keys lies on a line locked in the editor. */
export function onLockedLine(s: Pick<Services, 'store' | 'editor'>, ids: Iterable<KeyId>): boolean {
  const store = s.store;
  const locked = s.editor.getState().filters.lockedLines;
  if (store === null || locked.size === 0) return false;
  for (const id of ids) {
    const ref = columnOfKey(store.doc, id);
    if (ref !== null && locked.has(ref.line)) return true;
  }
  return false;
}

/** Why the selected segments cannot be changed (none, locked line), or null. */
export function cannotEditSegments(s: Pick<Services, 'store' | 'editor'>): MessageRef | null {
  if (s.store === null) return { key: m.noProject };
  const segs = selectedSegments(s);
  if (segs.length === 0) return { key: m.noSegments };
  return onLockedLine(s, segs) ? { key: m.lineLocked } : null;
}

/** Why the selected keys cannot be changed (none, locked line), or null. */
export function cannotEditKeys(s: Pick<Services, 'store' | 'editor'>): MessageRef | null {
  if (s.store === null) return { key: m.noProject };
  const keys = selectedKeys(s);
  if (keys.length === 0) return { key: m.noKeys };
  return onLockedLine(s, keys) ? { key: m.lineLocked } : null;
}

function numericKey(s: Pick<Services, 'store'>, id: KeyId): { readonly vIn: number; readonly vOut: number } | null {
  const k = s.store?.doc.keys.get(id);
  return k === undefined || typeof k.vIn !== 'number' || typeof k.vOut !== 'number'
    ? null
    : { vIn: k.vIn, vOut: k.vOut };
}

/** Selected keys with a jump (arrive value differs from leave value). */
export function selectedJumpKeys(s: Pick<Services, 'store' | 'editor'>): KeyId[] {
  return selectedKeys(s).filter((id) => {
    const k = numericKey(s, id);
    return k !== null && k.vIn !== k.vOut;
  });
}

/**
 * Selected continuous keys that may get a jump: never the first key of a curve (nothing arrives at it) nor the last
 * key of a GIF (it would stop playing).
 */
export function jumpCandidates(s: Pick<Services, 'store' | 'editor'>): KeyId[] {
  const store = s.store;
  if (store === null) return [];
  return selectedKeys(s).filter((id) => {
    const k = numericKey(s, id);
    if (k === null || k.vIn !== k.vOut || store.idx.prevKey(id) === undefined) return false;
    return !(columnOfKey(store.doc, id)?.prop === 'gif' && store.idx.nextKey(id) === undefined);
  });
}

/** Why "Make jump" is not available, or null. */
export function cannotMakeJump(s: Pick<Services, 'store' | 'editor'>): MessageRef | null {
  const why = cannotEditKeys(s);
  if (why !== null) return why;
  if (jumpCandidates(s).length > 0) return null;
  const continuous = selectedKeys(s).some((id) => {
    const k = numericKey(s, id);
    return k !== null && k.vIn === k.vOut;
  });
  return continuous ? { key: m.firstKeyJump } : { key: m.allJump };
}

/** Why "Continue from previous" is not available, or null. */
export function cannotRemoveJump(s: Pick<Services, 'store' | 'editor'>): MessageRef | null {
  const why = cannotEditKeys(s);
  if (why !== null) return why;
  return selectedJumpKeys(s).length === 0 ? { key: m.noJumpKeys } : null;
}

function easeOf(s: Pick<Services, 'store'>, key: KeyId): EaseValue | null {
  return s.store?.doc.keys.get(key)?.ease ?? null;
}

function propOf(s: Pick<Services, 'store'>, key: KeyId): LineProp | null {
  const store = s.store;
  return store === null ? null : (columnOfKey(store.doc, key)?.prop ?? null);
}

/** "Change curve…": the easing picker for the given segments; the choice is applied in one transaction. */
export async function changeCurve(s: Services, segments: readonly KeyId[]): Promise<void> {
  const store = s.store;
  const first = segments[0];
  if (store === null || first === undefined) {
    s.ui.toast({ key: m.noSegments }, { kind: 'warning' });
    return;
  }
  const prop = propOf(s, first) ?? 'moveX';
  const level = s.editor.getState().level;
  const ease = await s.ui.openDialog(DIALOG.easingPicker, {
    segments,
    initial: easeOf(s, first),
    use: easeUseFor(store.doc, prop, level),
  });
  if (ease === null) return;
  runTx(s, { key: m.txChangeCurve }, (tx) => Core.keys.setEase(tx, { segments, ease }));
}

/** Segments whose track may take a Bezier. */
function bezierSegments(s: Pick<Services, 'store'>, segments: readonly KeyId[]): KeyId[] {
  return segments.filter((id) => {
    const p = propOf(s, id);
    return p !== null && bezierAllowed(p);
  });
}

/** Why "Turn into a custom curve" is not available for the selection, or null. */
export function cannotMakeBezier(s: Pick<Services, 'store' | 'editor' | 'i18n'>): MessageRef | null {
  const segs = selectedSegments(s);
  if (segs.length === 0) return { key: m.noSegments };
  const allowed = bezierSegments(s, segs);
  if (allowed.length === 0) {
    const store = s.store;
    const ref = store === null || segs[0] === undefined ? null : columnOfKey(store.doc, segs[0]);
    const prop =
      store === null || ref === null ? '' : propLabel(ref.prop, unitContextOf(store.doc, store.idx, ref.line), s.i18n);
    return { key: m.bezierNone, params: { prop } };
  }
  if (!allowed.some((id) => easeOf(s, id)?.kind === 'tween')) return { key: m.noTweenSegments };
  return null;
}

/**
 * "Turn into a custom curve": every selected numbered-curve segment becomes the closest Bezier (fitted to the
 * curve Phira draws, slices included). Never on speed or effect variables.
 */
export function makeBezier(s: Services): void {
  const why = cannotMakeBezier(s);
  if (why !== null) {
    s.ui.toast(why, { kind: 'warning' });
    return;
  }
  let worst = 0;
  const plan: Array<{ readonly id: KeyId; readonly p: BezierPoints }> = [];
  for (const id of bezierSegments(s, selectedSegments(s))) {
    const e = easeOf(s, id);
    if (e?.kind !== 'tween') continue;
    const fit = fitBezier((x) => Ease.modelY(e, x));
    worst = Math.max(worst, fit.maxError);
    plan.push({ id, p: fit.p });
  }
  const ok = runTx(s, { key: m.txMakeBezier }, (tx) => {
    for (const { id, p } of plan) Core.keys.setEase(tx, { segments: [id], ease: { kind: 'bezier', p } });
    return true;
  });
  if (ok === true) {
    s.ui.toast(
      { key: m.bezierFitted, params: { percent: Math.round(worst * 1000) / 10 } },
      { kind: 'info', undo: true },
    );
  }
}

/** The first selected Bezier segment, or null. */
export function selectedBezier(s: Pick<Services, 'store' | 'editor'>): KeyId | null {
  for (const id of selectedSegments(s)) if (easeOf(s, id)?.kind === 'bezier') return id;
  return null;
}

/** "Edit the custom curve": the Bezier editor dialog for one segment. */
export async function editBezier(s: Services, segment: KeyId | null = selectedBezier(s)): Promise<void> {
  const e = segment === null ? null : easeOf(s, segment);
  if (segment === null || e?.kind !== 'bezier') {
    s.ui.toast({ key: m.noBezierSegment }, { kind: 'warning' });
    return;
  }
  const segments = [segment];
  const p = await s.ui.openDialog(DIALOG.bezierEditor, { initial: e.p, segments });
  if (p === null) return;
  runTx(s, { key: m.txBezier }, (tx) => Core.keys.setEase(tx, { segments, ease: { kind: 'bezier', p } }));
}

/** Selected segments that use part of a numbered curve. */
export function slicedSegments(s: Pick<Services, 'store' | 'editor'>): KeyId[] {
  return selectedSegments(s).filter((id) => {
    const e = easeOf(s, id);
    return e?.kind === 'tween' && (e.left !== 0 || e.right !== 1);
  });
}

/** "Use the whole curve": slices back to [0, 1]. */
export function resetSlice(s: Services, segments: readonly KeyId[] = slicedSegments(s)): void {
  if (segments.length === 0) {
    s.ui.toast({ key: m.noSlicedSegments }, { kind: 'warning' });
    return;
  }
  runTx(s, { key: m.txResetSlice }, (tx) => {
    for (const id of segments) {
      const e = tx.doc.keys.get(id)?.ease;
      if (e?.kind === 'tween')
        Core.keys.setEase(tx, { segments: [id], ease: { kind: 'tween', id: e.id, left: 0, right: 1 } });
    }
  });
}

/** "Stay, then jump" on the selected segments. */
export function toggleHold(s: Services): void {
  const why = cannotEditSegments(s);
  if (why !== null) {
    s.ui.toast(why, { kind: 'warning' });
    return;
  }
  const segments = selectedSegments(s);
  runTx(s, { key: m.txHold }, (tx) => {
    Core.keys.toggleHold(tx, { segments });
  });
}

/** Jump offset for "Make jump": a round step of about a tenth of the track's value range. */
export function jumpOffset(values: readonly number[]): number {
  let lo = Number.POSITIVE_INFINITY;
  let hi = Number.NEGATIVE_INFINITY;
  for (const v of values) {
    if (!Number.isFinite(v)) continue;
    lo = Math.min(lo, v);
    hi = Math.max(hi, v);
  }
  const span = hi > lo ? hi - lo : Math.max(1, Math.abs(Number.isFinite(lo) ? lo : 0));
  return niceStep(span / 10);
}

/** "Make jump" on the selected continuous keys (leave value offset; drag the half-diamonds to set it). */
export function makeJump(s: Services): void {
  const why = cannotMakeJump(s);
  if (why !== null) {
    s.ui.toast(why, { kind: 'warning' });
    return;
  }
  const keys = jumpCandidates(s);
  runTx(s, { key: m.txJump }, (tx) => {
    for (const id of keys) {
      const k = tx.doc.keys.get(id);
      if (k === undefined || typeof k.vIn !== 'number' || typeof k.vOut !== 'number' || k.vIn !== k.vOut) continue;
      const values = trackKeys(tx.doc, tx.idx, k.track).flatMap((x) =>
        typeof x.vOut === 'number' && typeof x.vIn === 'number' ? [x.vIn, x.vOut] : [],
      );
      Core.keys.makeJump(tx, { key: id, vIn: k.vIn, vOut: k.vIn + jumpOffset(values) });
    }
  });
}

/** "Continue from previous" (RPE Stick): selected jump keys leave at their arrive value. */
export function removeJump(s: Services): void {
  const why = cannotRemoveJump(s);
  if (why !== null) {
    s.ui.toast(why, { kind: 'warning' });
    return;
  }
  const keys = selectedJumpKeys(s);
  runTx(s, { key: m.txRemoveJump }, (tx) => {
    Core.keys.removeJump(tx, { keys, keep: 'in' });
  });
}

/** Deletes the selected keys (neighbouring segments merge; the core refuses the first key while others remain). */
export function deleteKeys(s: Services): void {
  const why = cannotEditKeys(s);
  if (why !== null) {
    s.ui.toast(why, { kind: 'warning' });
    return;
  }
  const keys = selectedKeys(s);
  const ok = runTx(s, { key: m.txDeleteKeys, params: { count: keys.length } }, (tx) => {
    Core.keys.deleteKeys(tx, { keys });
    tx.setSelection({ keys: [], segments: [] });
    return true;
  });
  // The deleted keys (and the segments they started) leave the selection right away.
  if (ok === true) s.editor.getState().select({ keys: new Set(keys), segments: new Set(keys) }, 'subtract');
}

/** Why a layer cannot be added to the expanded property, or null. */
export function cannotAddLayer(s: Pick<Services, 'store' | 'editor'>, ref: ColumnRef | null): MessageRef | null {
  const store = s.store;
  if (store === null) return { key: m.noProject };
  if (ref === null) return { key: m.noCurveEditor };
  if (!isLayered(ref.prop)) return { key: m.channelNeedsLayers };
  if (s.editor.getState().level !== 'full') return { key: m.channelFullOnly };
  if (layerCount(store.doc, store.idx, ref.line, ref.prop) >= MAX_UI_LAYERS) return { key: m.layerLimit };
  return null;
}

/** Adds a layer (one Stay key of 0 at beat 0) and shows it. */
export function addLayer(s: Services, ref: ColumnRef | null = expandedTarget(s)): void {
  const why = cannotAddLayer(s, ref);
  if (why !== null || ref === null) {
    s.ui.toast(why ?? { key: m.noCurveEditor }, { kind: 'warning' });
    return;
  }
  if (lineLocked(s, ref)) {
    s.ui.toast({ key: m.lineLocked }, { kind: 'warning' });
    return;
  }
  const layer = runTx(s, { key: m.txAddLayer }, (tx) => Core.keys.addLayer(tx, { line: ref.line, prop: ref.prop }));
  if (layer !== null) expandColumn(s, { ...ref, layer });
}

/** "Flatten to layer 0" through W2-11's contracted `tools.flatten` (it shows its own report). */
export async function flattenLayers(s: Services, ref: ColumnRef | null = expandedTarget(s)): Promise<void> {
  const store = s.store;
  if (store === null || ref === null) return;
  if (layerCount(store.doc, store.idx, ref.line, ref.prop) < 2) {
    s.ui.toast({ key: m.flattenNothing }, { kind: 'info' });
    return;
  }
  await s.registry.run('tools.flatten', { line: ref.line, prop: ref.prop });
}
